import { ITotalProgram, Scenario } from 'spw-redux/modules/calculators/types';
import _ from 'lodash';
import { GROWTH_CALCULATORS } from 'spw-constants';
import { IFloor } from 'spw-redux/modules/projects/types';
import {
  isDepartmentsTypeScenario,
  removeTypeNameFromObject,
} from 'spw-shared/utils';
import { PlainProgramScenario } from '../../models/Scenario';
import { PlainWorksheet } from '../../models/Worksheet';

export const generateLinkedScenariosIdsMap = (
  totalProgram: PlainProgramScenario
): {
  [scenarioId: number]: boolean;
} => {
  if (!totalProgram || !totalProgram.linkedScenarios) return {};
  return totalProgram.linkedScenarios.reduce((scenariosMap, s) => {
    return {
      ...scenariosMap,
      [s.id]: true,
    };
  }, {});
};

export const getTotalProgramLinkedScenarios = (
  totalProgram: ITotalProgram
): {
  [key: string]: boolean;
} => {
  if (!totalProgram || !totalProgram.totalProgramScenario) return {};
  return totalProgram.totalProgramScenario.reduce(
    (scenariosMap, _totalProgramScenario) => {
      return {
        ...scenariosMap,
        [_totalProgramScenario.scenarioId]: true,
      };
    },
    {}
  );
};

export const getScenarioFloors = (floors: IFloor[]) => {
  return floors.reduce(
    (floorsMap: { [key: number]: IFloor }, floor: IFloor) => {
      return {
        ...floorsMap,
        [floor.id]: floor,
      };
    },
    {}
  );
};

export const getWorksheetsByScenarioIdMap = (
  worksheets: PlainWorksheet[],
  isDownload?: boolean
): Record<number, PlainWorksheet> => {
  return worksheets.reduce((worksheetMap, worksheet: PlainWorksheet) => {
    return {
      ...worksheetMap,
      [worksheet.scenario.id]: _.omit(
        removeTypeNameFromObject({
          ...worksheet,
        }),
        ['worksheetRows', isDownload && 'rows']
      ),
    };
  }, {});
};

export const scenariosComparator = (
  scenarioA: Scenario | ITotalProgram | PlainProgramScenario,
  scenarioB: Scenario | ITotalProgram | PlainProgramScenario
) => {
  if (_.isNil(scenarioA.index) && !_.isNil(scenarioB.index)) {
    return -1;
  }

  if (!_.isNil(scenarioA.index) && _.isNil(scenarioB.index)) {
    return 1;
  }

  if (
    (_.isNil(scenarioA.index) && _.isNil(scenarioB.index)) ||
    scenarioA.index === scenarioB.index
  ) {
    return scenarioB.id - scenarioA.id;
  }

  return scenarioA.index - scenarioB.index;
};

export const sortScenarios = (
  scenarios: (Scenario | ITotalProgram)[] | PlainProgramScenario[]
) => {
  scenarios.sort(scenariosComparator);

  return scenarios;
};

export const sortFloorScenarios = (
  scenarios: PlainProgramScenario[],
  floors: { [id: number]: IFloor }
) => {
  scenarios.sort((scenarioA, scenarioB) => {
    const floorA = floors[scenarioA.floorId];
    const floorB = floors[scenarioB.floorId];

    if (floorA.floorNumber === floorB.floorNumber)
      return scenariosComparator(scenarioA, scenarioB);

    return floorA.floorNumber - floorB.floorNumber;
  });

  return scenarios;
};

export const getDepartmentScenarios = (
  scenarios: PlainProgramScenario[] = []
) => scenarios.filter(isDepartmentsTypeScenario);
export const getMacroProgrammingScenarios = (
  scenarios: PlainProgramScenario[] = []
) =>
  scenarios.filter(
    (scenario) =>
      scenario.calculatorType === GROWTH_CALCULATORS.macroProgramming
  );
export const getBuildingScenarios = (scenarios: PlainProgramScenario[] = []) =>
  scenarios.filter(
    (scenario) =>
      scenario.calculatorType === GROWTH_CALCULATORS.buildingUsf ||
      scenario.calculatorType === GROWTH_CALCULATORS.buildingHeadcountSeatcount
  );
export const getFloorScenarios = (
  scenarios: PlainProgramScenario[] = [],
  floorId: number
) =>
  scenarios.filter(
    (scenario) =>
      scenario.floorId === floorId &&
      (scenario.calculatorType === GROWTH_CALCULATORS.floorUsf ||
        scenario.calculatorType === GROWTH_CALCULATORS.floorHeadcountSeatcount)
  );
